import type { SuggestActionsConfig, SuggestContext, SuggestGroup, SuggestGroupId, SuggestRole } from '../agent.types';

// ─── Tile metadata (label + icon) ───────────────────────────────────────────

const TILE: Record<SuggestGroupId, { label: string; icon: string }> = {
  operations: { label: 'Operations', icon: 'fa-regular fa-briefcase' },
  scheduling: { label: 'Scheduling', icon: 'fa-regular fa-calendar' },
  customers:  { label: 'Customers',  icon: 'fa-regular fa-user' },
  finance:    { label: 'Finance',    icon: 'fa-regular fa-dollar-sign' },
};

// ─── Helper to build a group in one line ────────────────────────────────────

const g = (id: SuggestGroupId, prompts: string[]): SuggestGroup => ({ id, label: TILE[id].label, icon: TILE[id].icon, prompts });

// ─── The matrix ─────────────────────────────────────────────────────────────

export const SUGGEST_ACTIONS: Record<SuggestRole, Record<SuggestContext, SuggestActionsConfig>> = {

  administrator: {
    default: { groups: [
      g('operations', ['What needs my attention today?', 'Show me jobs stuck in the same stage for more than 7 days', 'Summarise activity from the last 24 hours']),
      g('scheduling', ['Any unassigned work orders this week?', 'Which jobs are behind their scheduled dates?', "What's scheduled for today?"]),
      g('finance',    ['Show overdue invoices', 'Any unpaid bills due this week?', 'Scan a new bill']),
      g('customers',  ['Top customers by job count this month', 'Customers with open jobs but no recent activity', 'Find a customer']),
    ]},
    job: { groups: [
      g('operations', ['Summarise this job', "What's the current status and next action?", 'Show all activity notes on this job']),
      g('scheduling', ['Is this job on schedule?', 'Show the stage timeline']),
      g('finance',    ["What's the invoiced value vs cost on this job?", 'Show all line items']),
      g('customers',  ["Show this customer's full history", 'What other open jobs does this customer have?']),
    ]},
    workOrder: { groups: [
      g('operations', ['Summarise this work order', "Who's assigned and what's the status?", 'Add an activity note']),
      g('scheduling', ['Reschedule this work order', 'Mark status (in progress / complete / on hold)']),
      g('finance',    ['What has been logged against this work order?']),
      g('customers',  ['Customer contact details']),
    ]},
  },

  operations: {
    default: { groups: [
      g('operations', ["What's on my plate today?", 'Which jobs are stuck waiting on something?', 'Show recent activity updates across all jobs']),
      g('scheduling', ["What's scheduled for today?", 'Any work orders without a technician assigned?', 'Which work orders are overdue?']),
      g('customers',  ['Find a customer', 'Show customers with open jobs needing follow-up']),
      g('finance',    ['Scan a new bill']),
    ]},
    job: { groups: [
      g('operations', ['Summarise this job', 'Add an activity note', 'Move this to the next stage']),
      g('scheduling', ['Update the stage dates', 'Change the scheduled start']),
      g('customers',  ['Get customer contact details']),
      g('finance',    ['Add line items to this job']),
    ]},
    workOrder: { groups: [
      g('operations', ['Update status (in progress / complete / on hold)', 'Add an activity note', 'Summarise this work order']),
      g('scheduling', ['Reschedule this work order', 'Reassign to another technician']),
      g('customers',  ['Get customer contact details']),
      g('finance',    ['Add line items']),
    ]},
  },

  scheduler: {
    default: { groups: [
      g('scheduling', ['What needs scheduling today?', 'Any unassigned work orders this week?', "Show me today's schedule"]),
      g('operations', ['Which jobs are ready to be scheduled?', 'Show jobs waiting on site access or parts']),
      g('customers',  ['Find a customer']),
      g('finance',    ['Scan a new bill']),
    ]},
    job: { groups: [
      g('scheduling', ['Update the stage dates', 'Show the stage timeline', 'Reschedule this job']),
      g('operations', ['Summarise this job', "What's blocking this job?"]),
      g('customers',  ['Customer availability / site access notes']),
      g('finance',    ['Add line items to this job']),
    ]},
    workOrder: { groups: [
      g('scheduling', ['Reschedule this work order', 'Reassign to another technician', 'Change the date/time']),
      g('operations', ['Summarise this work order', 'Mark as complete']),
      g('customers',  ['Customer contact details']),
      g('finance',    ['Show line items']),
    ]},
  },

  finance: {
    default: { groups: [
      g('finance',    ['Scan a new bill', 'Show overdue invoices', 'Bills due this week']),
      g('operations', ['Which jobs are ready to invoice?', 'Show completed jobs not yet invoiced']),
      g('customers',  ['Customers with outstanding balances', 'Top customers by revenue this month']),
      g('scheduling', ["What's scheduled for today?"]),
    ]},
    job: { groups: [
      g('finance',    ['Show all line items and totals', "What's the cost vs invoiced value?", 'Add line items to this job']),
      g('operations', ['Summarise this job']),
      g('customers',  ['Customer payment history']),
      g('scheduling', ['Is this job on schedule?']),
    ]},
    workOrder: { groups: [
      g('finance',    ['Log parts used', 'Show hours and costs on this work order', 'Add line items']),
      g('operations', ['Summarise this work order']),
      g('customers',  ['Customer contact details']),
      g('scheduling', ['Show the schedule']),
    ]},
  },

  fallback: {
    default: { groups: [
      g('operations', ['Summarise recent activity', 'Find a job', 'What needs attention today?']),
      g('scheduling', ["What's scheduled for today?", 'Find a work order']),
      g('customers',  ['Find a customer']),
      g('finance',    ['Scan a new bill']),
    ]},
    job: { groups: [
      g('operations', ['Summarise this job', 'Add an activity note']),
      g('scheduling', ['Show the schedule']),
      g('customers',  ['Customer details']),
      g('finance',    ['Show line items']),
    ]},
    workOrder: { groups: [
      g('operations', ['Summarise this work order', 'Add a note']),
      g('scheduling', ['Show schedule']),
      g('customers',  ['Customer details']),
      g('finance',    ['Line items']),
    ]},
  },

};
